import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Plus, Trash2, Edit, Eye, Check, X, RefreshCw, Send, 
  Upload, Package, CheckSquare, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Import admin components
import AdminLayout from '../components/admin/AdminLayout';
import B2BAnalyticsDashboard from '../components/admin/B2BAnalyticsDashboard';
import TrainingSuggestionsManager from '../components/admin/TrainingSuggestionsManager';
import OnboardingAnalyticsDashboard from '../components/admin/OnboardingAnalyticsDashboard';
import FeedbackDataDashboard from '../components/admin/FeedbackDataDashboard';
import AILearningDashboard from '../components/admin/AILearningDashboard';
import AdminSupportPanel from './admin/AdminSupportPanel';

const AdminPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('ai-learning');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [materials, setMaterials] = useState([]);
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [sampleRequests, setSampleRequests] = useState([]);
  const [advisorRequests, setAdvisorRequests] = useState([]);
  const [cityStats, setCityStats] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState({ plans: [], subscriptions: [], analytics: null });
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  
  // Form states
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState(null);
  
  const [materialForm, setMaterialForm] = useState({
    name: '', stone_type: 'Marble', color: 'White', finish: 'Polished',
    origin: '', description: '', price_range: '', images: []
  });

  // Redirect if not admin
  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        switch (activeTab) {
          case 'materials':
            const matsRes = await axios.get(`${API}/materials?limit=100`, { withCredentials: true });
            setMaterials(matsRes.data);
            break;
          case 'users':
            const usersRes = await axios.get(`${API}/admin/users`, { withCredentials: true });
            setUsers(usersRes.data);
            break;
          case 'approvals':
            const [pendingRes, advisorRes] = await Promise.all([
              axios.get(`${API}/admin/pending-users`, { withCredentials: true }).catch(() => ({ data: [] })),
              axios.get(`${API}/admin/advisor-requests`, { withCredentials: true }).catch(() => ({ data: [] }))
            ]);
            setPendingUsers(pendingRes.data);
            setAdvisorRequests(advisorRes.data);
            break;
          case 'samples':
            const samplesRes = await axios.get(`${API}/sample-requests`, { withCredentials: true }).catch(() => ({ data: [] }));
            setSampleRequests(samplesRes.data);
            break;
          case 'city':
            const cityRes = await axios.get(`${API}/admin/city-stats`, { withCredentials: true }).catch(() => ({ data: [] }));
            setCityStats(cityRes.data);
            break;
          case 'subscriptions':
            const [plansRes, subsRes, analyticsRes] = await Promise.all([
              axios.get(`${API}/subscriptions/plans`, { withCredentials: true }).catch(() => ({ data: [] })),
              axios.get(`${API}/subscriptions/all`, { withCredentials: true }).catch(() => ({ data: [] })),
              axios.get(`${API}/subscriptions/analytics`, { withCredentials: true }).catch(() => ({ data: null }))
            ]);
            setSubscriptionData({ plans: plansRes.data, subscriptions: subsRes.data, analytics: analyticsRes.data });
            break;
          case 'email':
            const emailRes = await axios.get(`${API}/admin/email-templates`, { withCredentials: true }).catch(() => ({ data: [] }));
            setEmailTemplates(emailRes.data);
            break;
          case 'settings':
            const settingsRes = await axios.get(`${API}/admin/settings`, { withCredentials: true }).catch(() => ({ data: null }));
            setSiteSettings(settingsRes.data);
            break;
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [activeTab]);

  // Material handlers
  const handleSaveMaterial = async () => {
    try {
      if (editingMaterial) {
        await axios.put(`${API}/materials/${editingMaterial.material_id}`, materialForm, { withCredentials: true });
        toast.success('Material updated');
      } else {
        await axios.post(`${API}/materials`, materialForm, { withCredentials: true });
        toast.success('Material created');
      }
      setShowMaterialForm(false);
      setEditingMaterial(null);
      // Refresh materials
      const res = await axios.get(`${API}/materials?limit=100`, { withCredentials: true });
      setMaterials(res.data);
    } catch (error) {
      toast.error('Failed to save material');
    }
  };

  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Delete this material?')) return;
    try {
      await axios.delete(`${API}/materials/${materialId}`, { withCredentials: true });
      toast.success('Material deleted');
      setMaterials(materials.filter(m => m.material_id !== materialId));
    } catch (error) {
      toast.error('Failed to delete material');
    }
  };

  // User handlers
  const handleApproveUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/approve-user/${userId}`, {}, { withCredentials: true });
      toast.success('User approved');
      setPendingUsers(pendingUsers.filter(u => u.user_id !== userId));
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleToggleBetaAccess = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/beta-access`, {
        user_id: userId,
        is_beta_user: !currentStatus
      }, { withCredentials: true });
      
      setUsers(users.map(u => 
        u.user_id === userId ? { ...u, is_beta_user: !currentStatus } : u
      ));
      toast.success(`Beta access ${!currentStatus ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update beta access');
    }
  };

  // Render content based on active tab
  const renderContent = () => {
    if (loading && !['ai-learning', 'feedback', 'training', 'onboarding', 'b2b'].includes(activeTab)) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      );
    }

    switch (activeTab) {
      // AI & DATA
      case 'ai-learning':
        return <AILearningDashboard />;
      case 'feedback':
        return <FeedbackDataDashboard />;
      case 'training':
        return <TrainingSuggestionsManager />;
      
      // SUPPORT
      case 'support':
        return <AdminSupportPanel adminUser={user} />;
      
      // MATERIALS
      case 'materials':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">{materials.length} materials in library</p>
              <Button onClick={() => { setEditingMaterial(null); setMaterialForm({ name: '', stone_type: 'Marble', color: 'White', finish: 'Polished', origin: '', description: '', price_range: '', images: [] }); setShowMaterialForm(true); }}>
                <Plus className="w-4 h-4 mr-2" /> Add Material
              </Button>
            </div>
            <div className="bg-white border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Material</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Type</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Color</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Origin</th>
                    <th className="text-right p-4 text-sm font-medium text-stone-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.slice(0, 50).map(mat => (
                    <tr key={mat.material_id} className="border-t hover:bg-stone-50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {mat.images?.[0] && (
                            <img src={mat.images[0]} alt={mat.name} className="w-10 h-10 rounded object-cover" />
                          )}
                          <span className="font-medium">{mat.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-sm">{mat.stone_type}</td>
                      <td className="p-4 text-sm">{mat.color}</td>
                      <td className="p-4 text-sm">{mat.origin || '-'}</td>
                      <td className="p-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingMaterial(mat); setMaterialForm(mat); setShowMaterialForm(true); }}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteMaterial(mat.material_id)} className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'approvals':
        return (
          <div className="space-y-8">
            {/* Pending Users */}
            <div>
              <h3 className="font-medium mb-4">Pending User Approvals ({pendingUsers.length})</h3>
              {pendingUsers.length === 0 ? (
                <p className="text-muted-foreground text-sm">No pending approvals</p>
              ) : (
                <div className="bg-white border rounded-lg divide-y">
                  {pendingUsers.map(u => (
                    <div key={u.user_id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{u.first_name} {u.last_name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleApproveUser(u.user_id)}>
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive">
                          <X className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Advisor Requests */}
            <div>
              <h3 className="font-medium mb-4">Advisor Requests ({advisorRequests.length})</h3>
              {advisorRequests.length === 0 ? (
                <p className="text-muted-foreground text-sm">No advisor requests</p>
              ) : (
                <div className="bg-white border rounded-lg divide-y">
                  {advisorRequests.map(req => (
                    <div key={req.request_id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{req.architect_name}</p>
                        <p className="text-sm text-muted-foreground">{req.advisor_name}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${req.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
                        {req.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      
      case 'bulk':
        return (
          <div className="max-w-xl">
            <div className="bg-white border rounded-lg p-8 text-center">
              <Upload className="w-12 h-12 mx-auto text-stone-400 mb-4" />
              <h3 className="font-medium mb-2">Bulk Import Materials</h3>
              <p className="text-sm text-muted-foreground mb-4">Upload a CSV or Excel file with material data</p>
              <Button>
                <Upload className="w-4 h-4 mr-2" /> Choose File
              </Button>
            </div>
          </div>
        );
      
      case 'samples':
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">{sampleRequests.length} sample requests</p>
            <div className="bg-white border rounded-lg divide-y">
              {sampleRequests.length === 0 ? (
                <p className="p-4 text-muted-foreground text-sm">No sample requests</p>
              ) : (
                sampleRequests.map(req => (
                  <div key={req.request_id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{req.material_name}</p>
                      <p className="text-sm text-muted-foreground">{req.user_name}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      req.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      req.status === 'shipped' ? 'bg-[var(--gold)]/20 text-[var(--gold)]' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {req.status}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      
      // USERS
      case 'users':
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">{users.length} registered users</p>
            <div className="bg-white border rounded-lg overflow-x-auto">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Name</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Email</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Role</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Beta Access</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.user_id} className="border-t hover:bg-stone-50">
                      <td className="p-4 font-medium">
                        {u.first_name} {u.last_name}
                        {u.is_admin && <span className="ml-2 text-xs bg-primary text-white px-1.5 py-0.5 rounded">Admin</span>}
                      </td>
                      <td className="p-4 text-sm">{u.email}</td>
                      <td className="p-4 text-sm capitalize">{u.role?.replace('_', ' ') || '-'}</td>
                      <td className="p-4">
                        <button
                          onClick={() => handleToggleBetaAccess(u.user_id, u.is_beta_user)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            u.is_beta_user ? 'bg-sr-gold' : 'bg-stone-300'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            u.is_beta_user ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </td>
                      <td className="p-4 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'advisor':
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">Manage advisor assignments and relationships</p>
            <div className="bg-white border rounded-lg p-8 text-center">
              <p className="text-muted-foreground">Advisor management interface</p>
            </div>
          </div>
        );
      
      case 'advisor-groups':
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">Organize advisors into groups</p>
            <div className="bg-white border rounded-lg p-8 text-center">
              <p className="text-muted-foreground">Advisor groups interface</p>
            </div>
          </div>
        );
      
      // ANALYTICS
      case 'stats':
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">Platform statistics overview</p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white border rounded-lg p-6">
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-3xl font-bold">{users.length || 0}</p>
              </div>
              <div className="bg-white border rounded-lg p-6">
                <p className="text-sm text-muted-foreground">Total Materials</p>
                <p className="text-3xl font-bold">{materials.length || 0}</p>
              </div>
              <div className="bg-white border rounded-lg p-6">
                <p className="text-sm text-muted-foreground">Sample Requests</p>
                <p className="text-3xl font-bold">{sampleRequests.length || 0}</p>
              </div>
              <div className="bg-white border rounded-lg p-6">
                <p className="text-sm text-muted-foreground">Active Subscriptions</p>
                <p className="text-3xl font-bold">{subscriptionData.subscriptions?.length || 0}</p>
              </div>
            </div>
          </div>
        );
      
      case 'city':
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">User distribution by city</p>
            <div className="bg-white border rounded-lg">
              <table className="w-full">
                <thead className="bg-stone-50">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">City</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Users</th>
                    <th className="text-left p-4 text-sm font-medium text-stone-600">Searches</th>
                  </tr>
                </thead>
                <tbody>
                  {cityStats.length === 0 ? (
                    <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">No city data</td></tr>
                  ) : (
                    cityStats.map((city, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-4 font-medium">{city.city}</td>
                        <td className="p-4">{city.users}</td>
                        <td className="p-4">{city.searches}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'b2b':
        return <B2BAnalyticsDashboard />;
      
      case 'onboarding':
        return <OnboardingAnalyticsDashboard />;
      
      // SYSTEM
      case 'settings':
        return (
          <div className="max-w-2xl space-y-6">
            <div className="bg-white border rounded-lg p-6 space-y-4">
              <h3 className="font-medium">Site Settings</h3>
              <div className="space-y-4">
                <div>
                  <Label>Site Name</Label>
                  <Input 
                    value={siteSettings?.branding?.site_name || ''} 
                    onChange={(e) => setSiteSettings({
                      ...siteSettings,
                      branding: { ...siteSettings?.branding, site_name: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <Label>Logo URL</Label>
                  <Input 
                    value={siteSettings?.branding?.logo_url || ''} 
                    onChange={(e) => setSiteSettings({
                      ...siteSettings,
                      branding: { ...siteSettings?.branding, logo_url: e.target.value }
                    })}
                  />
                </div>
                <Button onClick={async () => {
                  try {
                    await axios.put(`${API}/admin/settings`, siteSettings, { withCredentials: true });
                    toast.success('Settings saved');
                  } catch (e) {
                    toast.error('Failed to save settings');
                  }
                }}>
                  Save Settings
                </Button>
              </div>
            </div>
          </div>
        );
      
      case 'email':
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">Manage email templates</p>
            <div className="bg-white border rounded-lg divide-y">
              {emailTemplates.length === 0 ? (
                <p className="p-4 text-muted-foreground text-sm">No email templates</p>
              ) : (
                emailTemplates.map(template => (
                  <div key={template.template_id} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{template.name}</p>
                      <p className="text-sm text-muted-foreground">{template.subject}</p>
                    </div>
                    <Button variant="outline" size="sm">
                      <Edit className="w-4 h-4 mr-1" /> Edit
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      
      case 'subscriptions':
        return (
          <div className="space-y-6">
            {/* Plans */}
            <div>
              <h3 className="font-medium mb-4">Subscription Plans</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {subscriptionData.plans.map(plan => (
                  <div key={plan.plan_id} className="bg-white border rounded-lg p-6">
                    <h4 className="font-semibold text-lg">{plan.name}</h4>
                    <p className="text-2xl font-bold mt-2">${plan.price}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                    <ul className="mt-4 space-y-2 text-sm">
                      {plan.features?.map((f, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-green-500" /> {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Active Subscriptions */}
            <div>
              <h3 className="font-medium mb-4">Active Subscriptions ({subscriptionData.subscriptions.length})</h3>
              <div className="bg-white border rounded-lg">
                <table className="w-full">
                  <thead className="bg-stone-50">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">User</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Plan</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Status</th>
                      <th className="text-left p-4 text-sm font-medium text-stone-600">Expires</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptionData.subscriptions.slice(0, 20).map(sub => (
                      <tr key={sub.subscription_id} className="border-t">
                        <td className="p-4 text-sm">{sub.user_email || sub.user_id}</td>
                        <td className="p-4 text-sm font-medium">{sub.plan_name}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${
                            sub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-700'
                          }`}>
                            {sub.status}
                          </span>
                        </td>
                        <td className="p-4 text-sm">{sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto text-stone-400 mb-4" />
            <p className="text-muted-foreground">Select a section from the sidebar</p>
          </div>
        );
    }
  };

  // Redirect if not logged in
  if (!user) {
    return null;
  }

  return (
    <AdminLayout activeTab={activeTab} onTabChange={setActiveTab}>
      {renderContent()}
      
      {/* Material Form Dialog */}
      <Dialog open={showMaterialForm} onOpenChange={setShowMaterialForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingMaterial ? 'Edit Material' : 'Add Material'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name</Label>
              <Input 
                value={materialForm.name} 
                onChange={(e) => setMaterialForm({...materialForm, name: e.target.value})}
              />
            </div>
            <div>
              <Label>Stone Type</Label>
              <Select value={materialForm.stone_type} onValueChange={(v) => setMaterialForm({...materialForm, stone_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Marble', 'Granite', 'Quartzite', 'Onyx', 'Travertine', 'Limestone', 'Basalt', 'Slate'].map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Color</Label>
              <Select value={materialForm.color} onValueChange={(v) => setMaterialForm({...materialForm, color: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['White', 'Grey', 'Black', 'Beige', 'Brown', 'Green', 'Blue', 'Red', 'Pink', 'Yellow', 'Purple'].map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Finish</Label>
              <Select value={materialForm.finish} onValueChange={(v) => setMaterialForm({...materialForm, finish: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['Polished', 'Honed', 'Brushed', 'Leathered', 'Flamed'].map(f => (
                    <SelectItem key={f} value={f}>{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Origin</Label>
              <Input 
                value={materialForm.origin} 
                onChange={(e) => setMaterialForm({...materialForm, origin: e.target.value})}
              />
            </div>
            <div>
              <Label>Price Range</Label>
              <Input 
                value={materialForm.price_range} 
                onChange={(e) => setMaterialForm({...materialForm, price_range: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Textarea 
                value={materialForm.description} 
                onChange={(e) => setMaterialForm({...materialForm, description: e.target.value})}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowMaterialForm(false)}>Cancel</Button>
            <Button onClick={handleSaveMaterial}>Save Material</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminPanel;
